import {
  CONTRACT_TYPE_NO_AI_PROPOSAL_LIST_CAP,
  isAiDecidedRow,
  type ContractTypeConfirmedNewerRow,
  type ContractTypeProposalsResponse,
  type ContractTypeUndecidedReason,
  type ContractTypeUndecidedRow,
} from '@clm/shared-types';

import type { TypeProposalsTab } from './type-proposal-copy';

/**
 * The pure half of the Type proposals tabs — CLM-118 (R-118-16). Which listed
 * row sits on which tab, each tab's full count, and the cap line. Shared by
 * the Type proposals page and the Needs Attention panel, which shows the same
 * counts; kept out of the component files so fast refresh stays whole.
 */

type Row = ContractTypeUndecidedRow | ContractTypeConfirmedNewerRow;

/** The seven tabs' labels, shared with the Needs Attention panel so the two never name a list differently. */
export const TYPE_PROPOSALS_TAB_LABEL: Readonly<Record<TypeProposalsTab, string>> = {
  disagrees: 'AI disagrees',
  ai_decided: 'AI decided — verify',
  pending: 'Type pending',
  agrees: 'AI agrees — verify',
  not_read: 'Not read by AI',
  newer: 'Confirmed — a newer reading disagrees',
  amendments: 'Possibly an amendment',
};

export const TYPE_PROPOSALS_TABS: readonly TypeProposalsTab[] = [
  'disagrees',
  'ai_decided',
  'pending',
  'agrees',
  'not_read',
  'newer',
  'amendments',
];

const NOT_READ_REASONS: readonly ContractTypeUndecidedReason[] = ['restricted', 'awaiting_rerun', 'no_ai_proposal'];

function isPending(row: ContractTypeUndecidedRow): boolean {
  return row.seen.confirmation === 'pending';
}

export function typeProposalRowsOf(
  response: ContractTypeProposalsResponse,
  tab: Exclude<TypeProposalsTab, 'amendments'>,
): readonly Row[] {
  const provisional = response.undecided.filter((row) => !isPending(row));
  switch (tab) {
    case 'disagrees':
      return provisional.filter((row) => row.reason === 'ai_disagrees' || row.reason === 'ai_no_type');
    case 'ai_decided':
      return provisional.filter(isAiDecidedRow);
    case 'pending':
      return response.undecided.filter(isPending);
    case 'agrees':
      return provisional.filter((row) => row.reason === 'ai_agrees' && !isAiDecidedRow(row));
    case 'not_read':
      return provisional.filter((row) => NOT_READ_REASONS.includes(row.reason));
    case 'newer':
      return response.confirmed_newer_disagreements;
  }
}

/**
 * Each tab's full count — listed or not, so a capped list still says how many
 * there are. The totals count by reason whatever the confirmation; the Type
 * pending tab takes every pending agreement (`totals.pending`), so each other
 * tab's count is its reasons' total less the pending rows among them. Every
 * reason but `no_ai_proposal` is listed in full, so its pending rows are
 * counted from the list; the pending rows with no reading are what is left of
 * `totals.pending`, listed or not.
 */
export function typeProposalTabCounts(response: ContractTypeProposalsResponse): Readonly<Record<TypeProposalsTab, number>> {
  const totals = response.totals;
  const listedPending = (reason: ContractTypeUndecidedReason): number =>
    response.undecided.filter((row) => isPending(row) && row.reason === reason).length;
  const pendingFullyListed = response.undecided.filter((row) => isPending(row) && row.reason !== 'no_ai_proposal').length;
  const pendingNoReading = totals.pending - pendingFullyListed;
  return {
    disagrees: totals.ai_disagrees + totals.ai_no_type - listedPending('ai_no_type'),
    ai_decided: totals.ai_decided,
    pending: totals.pending,
    agrees: totals.ai_agrees - totals.ai_decided,
    not_read:
      totals.restricted -
      listedPending('restricted') +
      totals.awaiting_rerun -
      listedPending('awaiting_rerun') +
      totals.no_ai_proposal -
      pendingNoReading,
    newer: totals.confirmed_newer_disagreements,
    amendments: totals.amendment_signals,
  };
}

/** The cap line for a tab whose no-reading rows the API capped, or `null` when nothing was left out. */
export function typeProposalCapLine(response: ContractTypeProposalsResponse, tab: TypeProposalsTab): string | null {
  const totals = response.totals;
  if (totals.no_ai_proposal_listed >= totals.no_ai_proposal) return null;
  const cap = `the list of contracts with no AI reading is capped at ${String(CONTRACT_TYPE_NO_AI_PROPOSAL_LIST_CAP)}, Type pending first`;
  if (tab === 'pending') {
    const listed = response.undecided.filter(isPending).length;
    return listed < totals.pending
      ? `Showing ${String(listed)} of ${String(totals.pending)} Type pending contracts (${cap}).`
      : null;
  }
  if (tab !== 'not_read') return null;
  const listed = response.undecided.filter((row) => !isPending(row) && row.reason === 'no_ai_proposal').length;
  const pendingNoReading =
    totals.pending - response.undecided.filter((row) => isPending(row) && row.reason !== 'no_ai_proposal').length;
  return `Showing the newest ${String(listed)} of ${String(totals.no_ai_proposal - pendingNoReading)} contracts with no AI reading (${cap}).`;
}
